// ═══════════════════════════════════════════════════════════════════════
// 7FCC LANDING PAGE — SINGLE SOURCE OF CONTENT
// Short, focused, high-conversion. Every headline, paragraph and
// placeholder is edited HERE — no component edits required.
// ═══════════════════════════════════════════════════════════════════════

export const BRAND = {
  name: "7 Figure Closing Club",
  short: "7FCC",
};

export const IMAGES = {
  logo: "/7fcc-logo-float.png",
  logoAlt: "7 Figure Closing Club chrome logo",
};

export const CTA = {
  label: "See If You Qualify",
  targetId: "qualify",
  note: "60-second application. No sales experience required.",
};

export const HERO = {
  eyebrow: "High-Ticket Sales Training",
  headlineLines: [
    "How Regular People Are Making",
    "$15k/Month From Anywhere in the World",
    "With One Valuable Skillset",
  ],
  subhead:
    "Learn the skill businesses already pay for, practice it with real coaching, and see if 7FCC is the right fit for you.",
};

export const VSL = {
  eyebrow: "Watch This First",
  headline: "How does it work?",
  support:
    "Watch this quick breakdown of the training, the skill, and what it takes to succeed.",
  videoLabel: "[VSL VIDEO PLACEHOLDER]",
  caption: "Watch the full video before you answer the questions below.",
  // ── VSL VIDEO SOURCE ─────────────────────────────────────────────
  // Set this to the video's direct URL and the placeholder swaps to a
  // custom autoplaying VSL player: no controls, no timeline, no
  // scrubbing, no download/PiP — and it scrolls to the form when it ends.
  // Examples: "/videos/vsl.mp4" (file uploaded alongside the site on
  // Netlify) or "https://cdn.bunny.net/.../vsl.mp4" (any direct file URL).
  // Browsers force autoplay to start muted — the player shows a premium
  // "Tap to unmute" button and tries full sound first.
  videoSrc: "https://assets.cdn.filesafe.space/CscG0b1eU7e07D3fczKL/media/6ac4172b5be7b18197910cc7.mp4",
};

export const OPPORTUNITY = {
  eyebrow: "The Opportunity",
  headline: "Why High-Ticket Sales?",
  intro:
    "Instead of building your own product or business from scratch, high-ticket closers develop a sales skill they can use with companies that already have something to sell.",
  cards: [
    {
      title: "No Business to Build",
      desc: "Focus on developing a valuable sales skill instead of creating your own product, fulfillment, or company from scratch.",
    },
    {
      title: "No Large Following Required",
      desc: "You don't need thousands of followers or a massive personal network to learn the skill.",
    },
    {
      title: "Work From Anywhere",
      desc: "High-ticket sales can be performed remotely for many online businesses with a laptop, phone, and internet connection.",
    },
  ],
  steps: [
    { title: "Learn the skill", desc: "Develop the fundamentals of high-ticket sales." },
    { title: "Get on an offer", desc: "Use the skill selling an existing company's product or service." },
    { title: "Earn commission", desc: "Get compensated based on sales performance." },
  ],
};

export const PROOF = {
  headline: "Real Students. Real Progress.",
  support:
    "A few examples of what happens when students put the training into practice.",
  // Real student proof screenshots — more batches can be appended here.
  items: [
    {
      src: "/proof/proof-01.png",
      alt: "Facebook post from Joseph Ortega congratulating Salvador on closing a $10K deal",
    },
    {
      src: "/proof/proof-02.png",
      alt: "Message from Betza Rojas: 10K paid in full — 'too easy'",
    },
    {
      src: "/proof/proof-03.png",
      alt: "Team chat showing a $5K paid-in-full close and a $5,000 succeeded payment confirmation",
    },
    {
      src: "/proof/proof-04.png",
      alt: "Daily revenue board showing $41,000 in total with top setters and closers",
    },
    {
      src: "/proof/proof-05.png",
      alt: "Facebook post celebrating Mark's first $100+ win and Sarah for setting the appointment",
    },
    {
      src: "/proof/proof-06.png",
      alt: "Team chat celebrating the top closer of the week",
    },
    {
      src: "/proof/proof-07.png",
      alt: "Facebook post from Elijah Allah — collected $13,500 cash in a day as a closer",
    },
    {
      src: "/proof/proof-08.png",
      alt: "Team chat celebrating a $3,325 payment made within five days",
    },
  ],
  featured: {
    src: "/proof/proof-featured.png",
    alt: "Facebook post announcing Brittany's Elite Closer Certification after generating over $200K in cash collected",
    name: "Brittany",
    result: "$200K+ Collected In Under Two Months",
    text:
      "\u201CCame into this program less than two months ago with zero closing experience and has generated over $200K in cash collected as a closer. She has now received her credentials as a certified closer.\u201D",
  },
};

// Vertical video testimonials — shown compactly after the proof wall.
export const VIDEO_TESTIMONIALS_CAPTION =
  "Don't take our word for it. Hear it from the students putting in the reps.";

// caption is optional (name + one-line result). Empty string = render nothing.
export const VIDEO_TESTIMONIALS = [
  {
    src: "https://assets.cdn.filesafe.space/CscG0b1eU7e07D3fczKL/media/6ac42ce77d735ea2a14a21ba.mp4",
    alt: "Student video testimonial 1",
    caption: "",
  },
  {
    src: "https://assets.cdn.filesafe.space/CscG0b1eU7e07D3fczKL/media/6ac44a69195f9172ed994186.mp4",
    alt: "Student video testimonial 2",
    caption: "",
  },
  {
    src: "https://assets.cdn.filesafe.space/CscG0b1eU7e07D3fczKL/media/6ac44a69195f9172ed99417b.mp4",
    alt: "Student video testimonial 3",
    caption: "",
  },
  {
    src: "https://assets.cdn.filesafe.space/CscG0b1eU7e07D3fczKL/media/6ac42ce7b12649a51231507c.mp4",
    alt: "Student video testimonial 4",
    caption: "",
  }
];

export const QUIZ = {
  eyebrow: "Your Next Step",
  headline: "Is this a good fit for you?",
  support:
    "Answer a few quick questions so we can understand where you're at and what you're looking for.",
  questions: [
    {
      q: "Where are you right now?",
      options: [
        "I'm starting from zero",
        "I have some sales experience",
        "I'm already setting appointments",
        "I'm already closing deals",
      ],
    },
    {
      q: "What are you trying to accomplish?",
      options: [
        "Learn a remote income skill",
        "Move into high-ticket sales",
        "Increase what I'm earning",
        "Improve my current sales skills",
      ],
    },
    {
      q: "How soon are you looking to make a change?",
      options: ["Right now", "Within 30 days", "Within 90 days", "I'm just exploring"],
    },
  ],
  form: {
    headline: "Good. Now let's see if there's a fit.",
    fields: [
      { name: "firstName", label: "First Name", type: "text" },
      { name: "lastName", label: "Last Name", type: "text" },
      { name: "email", label: "Email", type: "email" },
      { name: "phone", label: "Phone", type: "tel" },
    ],
    button: "Continue My Application",
  },
};

export const FAQ = {
  heading: "Questions before you apply?",
  items: [
    {
      q: "Do I need sales experience?",
      a: "No. Some students start from zero and others come in with experience. The training is designed to build the fundamentals and improve through practice.",
    },
    {
      q: "Is this just an online course?",
      a: "No. 7FCC combines training with coaching, roleplay, repetition and community so students can actually practice the skill.",
    },
    {
      q: "Can I do this remotely?",
      a: "Yes. The training and community are built for remote participation.",
    },
    {
      q: "What happens after I apply?",
      a: "We'll review where you're at and what you're trying to accomplish. If it makes sense to continue, you'll receive the next step to speak with the team.",
    },
  ],
};

export const FOOTER = {
  // Placeholder hrefs — attach the real Privacy Policy / Terms / Contact
  // URLs here. Do not publish legal copy until it is reviewed.
  links: [
    { label: "Privacy Policy", href: "#" },
    { label: "Terms", href: "#" },
    { label: "Contact", href: "#" },
  ],
  copyright: "Copyright © 7FCC",
};

// ═══════════════════════════════════════════════════════════════════════
// SOCIAL-PROOF WIDGET — TEMPORARY DEMO ACTIVITY DATA
// These entries are demo placeholders rotating in the bottom-left
// notification. Replace with real (consented) activity data or wire to
// the CRM feed before driving live traffic.
// ═══════════════════════════════════════════════════════════════════════
export const ACTIVITY_NOTIFICATIONS = [
  { firstName: "Jessica", location: "Jacksonville, FL", action: "just booked her Success Call" },
  { firstName: "Marcus", location: "Dallas, TX", action: "just applied to 7FCC" },
  { firstName: "Ashley", location: "Phoenix, AZ", action: "just booked a call with the 7FCC team" },
  { firstName: "Chris", location: "Orlando, FL", action: "just submitted an application" },
  { firstName: "Taylor", location: "Austin, TX", action: "just booked a Success Call" },
  { firstName: "Brianna", location: "Miami, FL", action: "just submitted an application" },
  { firstName: "Jordan", location: "Atlanta, GA", action: "just booked his Success Call" },
  { firstName: "Kevin", location: "Chicago, IL", action: "just applied to 7FCC" },
  { firstName: "Samantha", location: "Denver, CO", action: "just booked a call with the 7FCC team" },
  { firstName: "Tyler", location: "Nashville, TN", action: "just booked her Success Call" },
  { firstName: "Brittany", location: "Charlotte, NC", action: "just submitted an application" },
  { firstName: "Andre", location: "Houston, TX", action: "just booked a Success Call" },
  { firstName: "Megan", location: "Portland, OR", action: "just applied to 7FCC" },
  { firstName: "Devonte", location: "Detroit, MI", action: "just booked a call with the 7FCC team" },
  { firstName: "Kayla", location: "Tampa, FL", action: "just submitted an application" },
  { firstName: "Robert", location: "Phoenix, AZ", action: "just booked his Success Call" },
  { firstName: "Jasmine", location: "Newark, NJ", action: "just applied to 7FCC" },
  { firstName: "Cody", location: "Boise, ID", action: "just submitted an application" },
  { firstName: "Alexis", location: "San Diego, CA", action: "just booked a call with the 7FCC team" },
  { firstName: "Malik", location: "Philadelphia, PA", action: "just booked her Success Call" },
  { firstName: "Heather", location: "Columbus, OH", action: "just submitted an application" },
  { firstName: "Sean", location: "Boston, MA", action: "just applied to 7FCC" },
  { firstName: "Victoria", location: "Las Vegas, NV", action: "just booked a Success Call" },
  { firstName: "Isaiah", location: "Memphis, TN", action: "just booked a call with the 7FCC team" },
  { firstName: "Rachel", location: "Minneapolis, MN", action: "just submitted an application" },
  { firstName: "Eric", location: "Salt Lake City, UT", action: "just booked his Success Call" },
  { firstName: "Lauren", location: "St. Louis, MO", action: "just applied to 7FCC" },
  { firstName: "Jamal", location: "Baltimore, MD", action: "just booked a call with the 7FCC team" },
  { firstName: "Brooke", location: "Indianapolis, IN", action: "just submitted an application" },
  { firstName: "Trevor", location: "Kansas City, MO", action: "just booked her Success Call" },
  { firstName: "Monica", location: "El Paso, TX", action: "just applied to 7FCC" },
  { firstName: "Darius", location: "Cleveland, OH", action: "just submitted an application" },
  { firstName: "Stephanie", location: "Tucson, AZ", action: "just booked a Success Call" },
  { firstName: "Blake", location: "Oklahoma City, OK", action: "just booked a call with the 7FCC team" },
  { firstName: "Amber", location: "Pittsburgh, PA", action: "just submitted an application" },
];

// Live GoHighLevel qualification survey embedded in the #qualify card on /ready.
// After submit, GHL redirects to /apply (configured in GHL survey settings).
export const SURVEY_EMBED = {
  src: "https://api.leadconnectorhq.com/widget/survey/DtUsfuoEzkV8Z7FOXNtr",
  iframeId: "DtUsfuoEzkV8Z7FOXNtr",
  title: "7FCC Qualification Survey",
};
